import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar as CalendarIcon, Search, Filter, CheckCircle, Clock, XCircle, FileText, Download, Mail, Star, LogOut, X, AlertTriangle, MessageCircle, RefreshCw, Navigation, Trash2, MapPin, User, Phone, ExternalLink, Car, Plane, DollarSign, MessageSquare, Receipt, Bell, Users, Save } from 'lucide-react';
import { format, isToday, isTomorrow, isPast, parseISO } from 'date-fns';
import { getAllBookings, getAllReceipts, getAllSubscribers, updateSubscriber, deleteSubscriber, deleteBooking as deleteBookingAPI, resendConfirmationEmail, resendReceiptEmail, updateBookingFare, completeBooking, getRecentBookings } from '../utils/api';
import { formatTime12Hour } from '../utils/timeFormat';
import { useAuth } from '../context/AuthContext';
import { getApiBaseUrl } from '../utils/apiBase';
import BookingCalendar from '../components/BookingCalendar';
import UpcomingRidesWidget from '../components/UpcomingRidesWidget';
import SunMedInvoiceList from '../components/SunMedInvoiceList';
import SunMedInvoiceForm from '../components/SunMedInvoiceForm';
import SunMedInvoicePreview from '../components/SunMedInvoicePreview';
import AdminBlogPanel from '../components/AdminBlogPanel';
import CopyReviewMessageButton from '../components/CopyReviewMessageButton';
import BookingEmailStatus from '../components/BookingEmailStatus';

const INCOMPLETE_STATUSES = [
  'Scheduled',
  'Assigned',
  'Driver En Route',
  'Passenger Picked Up',
  'Arrived at Destination',
  'Payment Pending',
  'pending'
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [activeTab, setActiveTab] = useState('bookings'); // 'bookings' | 'sunmed' | 'visitors' | 'blog'

  // Visitor List (mailing list) admin state — 2026-06-06
  const [visitors, setVisitors] = useState([]);
  const [visitorsLoading, setVisitorsLoading] = useState(false);
  const [visitorsError, setVisitorsError] = useState('');
  const [visitorSearch, setVisitorSearch] = useState('');
  const [visitorStatusFilter, setVisitorStatusFilter] = useState('all');
  const [editingNotesId, setEditingNotesId] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [visitorToDelete, setVisitorToDelete] = useState(null);
  const [deletingVisitorId, setDeletingVisitorId] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateFilter, setDateFilter] = useState(null); // null, 'today', 'tomorrow', 'overdue', or specific date
  const [loading, setLoading] = useState(true);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [selectedBookingForTracking, setSelectedBookingForTracking] = useState(null);
  const [initializingTracking, setInitializingTracking] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBookingDetails, setSelectedBookingDetails] = useState(null);
  const [editingFare, setEditingFare] = useState(false);
  const [newActualFare, setNewActualFare] = useState('');
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionData, setCompletionData] = useState({
    actualFare: '',
    paymentMethod: 'cash',
    notes: ''
  });
  const [completing, setCompleting] = useState(false);

  // Sun Med Invoice States
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceRefreshKey, setInvoiceRefreshKey] = useState(0);

  // ===== Lightweight admin browser-notification system (P2) =====
  // Polls a skinny `/api/admin/bookings/recent` endpoint every 15s while the
  // dashboard is open so a freshly created booking pops a browser
  // notification (or an in-dashboard banner if permission is denied).
  // No service worker, no VAPID, no background push.
  const POLL_INTERVAL_VISIBLE = 15000;
  const POLL_INTERVAL_HIDDEN = 30000;
  const NOTIF_LAST_SEEN_KEY = 'taxi98_admin_lastSeenBookingAt';
  const NOTIF_PERMISSION_DISMISSED_KEY = 'taxi98_admin_notifBannerDismissed';
  const NOTIF_SOUND_ENABLED_KEY = 'taxi98_admin_notifSoundEnabled';

  const [notifPermission, setNotifPermission] = useState(
    typeof window !== 'undefined' && 'Notification' in window
      ? Notification.permission
      : 'unsupported'
  );
  const [showNotifBanner, setShowNotifBanner] = useState(false);
  const [pendingNewBookings, setPendingNewBookings] = useState([]); // unread alerts
  const [highlightedBookingId, setHighlightedBookingId] = useState(null);
  // LB-3: Optional sound toggle for new-booking alerts. Off by default, opt-in only,
  // persisted in localStorage. Does not affect existing fallback/browser-notification logic.
  const [soundEnabled, setSoundEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return localStorage.getItem(NOTIF_SOUND_ENABLED_KEY) === '1';
    } catch (_e) {
      return false;
    }
  });
  const handleToggleSound = () => {
    setSoundEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(NOTIF_SOUND_ENABLED_KEY, next ? '1' : '0');
      } catch (_e) { /* ignore quota / private mode */ }
      return next;
    });
  };
  const playNotifSound = () => {
    if (typeof window === 'undefined') return;
    if (!soundEnabled) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(660, ctx.currentTime + 0.18);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.32);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.34);
      // Auto-close to free resources
      osc.onended = () => { try { ctx.close(); } catch (_e) { /* ignore */ } };
    } catch (_e) {
      // Audio failure is non-fatal — visual alerts remain unchanged.
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCompleteBooking = async () => {
    if (!completionData.actualFare || parseFloat(completionData.actualFare) <= 0) {
      alert('Please enter a valid fare amount');
      return;
    }

    try {
      setCompleting(true);
      const result = await completeBooking(
        selectedBookingDetails.id,
        parseFloat(completionData.actualFare),
        completionData.paymentMethod,
        completionData.notes
      );

      // Close modals and refresh
      setShowCompletionModal(false);
      setShowDetailsModal(false);
      await fetchData();

      // Show success message with email + review-request status
      const reviewLine = (() => {
        if (result.reviewRequestSent) return '\n✅ Review request sent.';
        if (result.reviewRequestAlreadySent) return '\nℹ️ Review request was already sent earlier.';
        if (result.reviewRequestSkipped) return ''; // silent skip (e.g. manual ride placeholder)
        if (result.reviewRequestError) return `\n⚠️ Completed, but review request failed to send (${result.reviewRequestError}).`;
        return '';
      })();

      if (result.receiptSent) {
        alert(`✅ Trip completed successfully!\n✅ Receipt email sent to customer.${reviewLine}`);
      } else {
        alert(`✅ Trip completed successfully!\n⚠️ Receipt email failed: ${result.emailError || 'Unknown error'}\n\nYou can resend the receipt from the booking details.${reviewLine}`);
      }

      // Reset completion form
      setCompletionData({ actualFare: '', paymentMethod: 'cash', notes: '' });
    } catch (error) {
      console.error('Completion error:', error);
      alert(`Failed to complete trip: ${error.response?.data?.detail || error.message}`);
    } finally {
      setCompleting(false);
    }
  };

  const fetchData = async (retryCount = 0) => {
    try {
      setLoading(true);
      const [bookingsData, receiptsData] = await Promise.all([
        getAllBookings(),
        getAllReceipts()
      ]);
      setBookings(bookingsData);
      setReceipts(receiptsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      
      // Check if it's an auth error
      if (error.response?.status === 401) {
        console.error('Authentication error - token may be expired');
        alert('Your session has expired. Please login again.');
        logout();
        navigate('/admin');
        return;
      }
      
      // Retry once on failure
      if (retryCount === 0) {
        console.log('Retrying data fetch...');
        setTimeout(() => fetchData(1), 1000);
        return;
      }
      
      // Show error after retry
      console.error('Failed to load dashboard data after retry:', error.message);
      alert(`Failed to load dashboard data: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // ===== Notification permission management =====
  const requestNotifPermission = async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    try {
      const result = await Notification.requestPermission();
      setNotifPermission(result);
    } catch (err) {
      console.error('Notification permission request failed:', err);
    } finally {
      // Hide banner regardless — user has answered (granted/denied).
      setShowNotifBanner(false);
      try {
        localStorage.setItem(NOTIF_PERMISSION_DISMISSED_KEY, '1');
      } catch (_e) { /* localStorage may be unavailable */ }
    }
  };

  const dismissNotifBanner = () => {
    setShowNotifBanner(false);
    try { localStorage.setItem(NOTIF_PERMISSION_DISMISSED_KEY, '1'); } catch (_e) { /* ignore */ }
  };

  // Decide once whether to show the one-time permission banner.
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'default') return;
    let dismissed = false;
    try { dismissed = localStorage.getItem(NOTIF_PERMISSION_DISMISSED_KEY) === '1'; } catch (_e) { /* ignore */ }
    if (!dismissed) setShowNotifBanner(true);
  }, []);

  // ===== Lightweight polling for new bookings =====
  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const getLastSeen = () => {
      try {
        const v = localStorage.getItem(NOTIF_LAST_SEEN_KEY);
        if (v) return v;
      } catch (_e) { /* ignore */ }
      // First-ever poll: anchor to "now" so we don't alert on the entire history.
      const nowIso = new Date().toISOString();
      try { localStorage.setItem(NOTIF_LAST_SEEN_KEY, nowIso); } catch (_e) { /* ignore */ }
      return nowIso;
    };

    const setLastSeen = (iso) => {
      try { localStorage.setItem(NOTIF_LAST_SEEN_KEY, iso); } catch (_e) { /* ignore */ }
    };

    const fireBrowserNotification = (booking) => {
      if (typeof window === 'undefined' || !('Notification' in window)) return false;
      if (Notification.permission !== 'granted') return false;
      try {
        const body = `${booking.passengerName || 'Customer'} • ${booking.pickupZone || '?'} → ${booking.dropoffZone || '?'} • ${booking.date} ${booking.time}`;
        const n = new Notification('New booking — Taxi-98', {
          body,
          tag: `taxi98-new-booking-${booking.id}`, // dedupes if browser re-fires
          icon: '/favicon.ico',
        });
        n.onclick = () => {
          try { window.focus(); } catch (_e) { /* ignore */ }
          setActiveTab('bookings');
          setHighlightedBookingId(booking.id);
          // Best-effort scroll
          setTimeout(() => {
            const el = document.querySelector(`[data-testid="booking-row-${booking.id}"]`);
            if (el && typeof el.scrollIntoView === 'function') {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 250);
          n.close();
        };
        return true;
      } catch (err) {
        console.error('Notification fire failed:', err);
        return false;
      }
    };

    const tick = async () => {
      if (cancelled) return;
      try {
        const since = getLastSeen();
        const res = await getRecentBookings(since);
        if (cancelled) return;
        const fresh = (res && res.bookings) || [];
        if (fresh.length > 0) {
          // Newest first from API; track max createdAt to advance the cursor.
          const maxCreatedAt = fresh.reduce((m, b) => (b.createdAt > m ? b.createdAt : m), since);
          // Try OS-level notifications; fall back to in-dashboard banner for
          // any bookings the OS notification couldn't fire.
          const queued = [];
          fresh.forEach((b) => {
            const fired = fireBrowserNotification(b);
            if (!fired) queued.push(b);
          });
          // LB-3: optional audible chime (opt-in, off by default).
          if (fresh.length > 0) {
            playNotifSound();
          }
          if (queued.length > 0) {
            setPendingNewBookings((prev) => {
              // Dedupe by id to be safe.
              const ids = new Set(prev.map((p) => p.id));
              const merged = [...prev];
              queued.forEach((q) => { if (!ids.has(q.id)) merged.push(q); });
              return merged;
            });
          }
          // Refresh the bookings list so the new rows show up immediately.
          fetchData();
          setLastSeen(maxCreatedAt);
        }
      } catch (err) {
        // Stay quiet on poll errors — next tick will retry.
        console.debug('Recent-bookings poll error:', err?.response?.status || err?.message || err);
      }
    };

    const schedule = () => {
      const interval = (typeof document !== 'undefined' && document.visibilityState === 'hidden')
        ? POLL_INTERVAL_HIDDEN
        : POLL_INTERVAL_VISIBLE;
      timer = setTimeout(async () => {
        await tick();
        if (!cancelled) schedule();
      }, interval);
    };

    // Kick off first tick immediately, then schedule subsequent ones.
    tick().then(() => { if (!cancelled) schedule(); });

    const onVisibilityChange = () => {
      // Restart cadence with the appropriate interval when visibility changes.
      if (timer) clearTimeout(timer);
      if (!cancelled) schedule();
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
    };
    // Dependencies kept empty: poll loop should outlive other state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearPendingBookings = () => {
    setPendingNewBookings([]);
  };

  const handleAlertClick = (bookingId) => {
    setActiveTab('bookings');
    setHighlightedBookingId(bookingId);
    setPendingNewBookings((prev) => prev.filter((b) => b.id !== bookingId));
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="booking-row-${bookingId}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // Auto-clear highlight after 6s
  useEffect(() => {
    if (!highlightedBookingId) return;
    const t = setTimeout(() => setHighlightedBookingId(null), 6000);
    return () => clearTimeout(t);
  }, [highlightedBookingId]);



  const handleExportCSV = () => {
    // Create CSV content
    const headers = ['Booking ID', 'Passenger', 'Email', 'Phone', 'Pickup', 'Dropoff', 'Date', 'Time', 'Status', 'Fare'];
    const rows = filteredBookings.map(b => [
      b.id,
      b.passengerName,
      b.passengerEmail,
      b.passengerPhone,
      b.pickupLocation,
      b.dropoffLocation,
      b.date,
      formatTime12Hour(b.time),
      b.status,
      `$${b.estimatedFare}`
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `taxi98-bookings-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportReceiptsCSV = () => {
    // Create CSV content for receipts
    const headers = ['Receipt Ref', 'Customer', 'Email', 'Pickup', 'Dropoff', 'Date', 'Time', 'Fare', 'Driver'];
    const rows = receipts.map(r => [
      r.reference,
      r.customerName,
      r.customerEmail,
      r.pickupLocation,
      r.dropoffLocation,
      r.date,
      formatTime12Hour(r.dropoffTime),
      `$${r.fareCollected}`,
      r.driverName
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `taxi98-receipts-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportMailingListCSV = async () => {
    try {
      // Use the already-loaded list if available, otherwise pull fresh.
      const subscribers = visitors && visitors.length > 0 ? visitors : await getAllSubscribers();

      // Updated 2026-06-06: include new fields (phone, notes, source, status).
      const headers = [
        'First Name',
        'Email',
        'Phone / WhatsApp',
        'City',
        'Country',
        'Source',
        'Notes',
        'Status',
        'Submitted Date',
      ];
      const rows = subscribers.map(s => [
        s.firstName || '',
        s.email || '',
        s.phone || '',
        s.city || '',
        s.country || '',
        s.source || '',
        (s.notes || '').replace(/"/g, '""'),
        formatVisitorStatusLabel(s.status),
        s.subscribedAt ? new Date(s.subscribedAt).toLocaleString() : '',
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Download
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `taxi98-mailing-list-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export mailing list:', error);
      alert('Failed to export mailing list');
    }
  };

  // ----- Visitor List helpers (2026-06-06) ------------------------------------

  // Friendly display label for a stored visitor status code.
  const formatVisitorStatusLabel = (status) => {
    const s = (status || 'new').toLowerCase();
    if (s === 'new') return 'New';
    if (s === 'contacted') return 'Contacted';
    if (s === 'booked') return 'Booked';
    if (s === 'not_interested') return 'Not Interested';
    return s;
  };

  const VISITOR_STATUS_OPTIONS = [
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'booked', label: 'Booked' },
    { value: 'not_interested', label: 'Not Interested' },
  ];

  const visitorStatusBadgeClass = (status) => {
    switch ((status || 'new').toLowerCase()) {
      case 'contacted':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'booked':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'not_interested':
        return 'bg-gray-100 text-gray-600 border-gray-200';
      case 'new':
      default:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
  };

  const loadVisitors = async () => {
    setVisitorsLoading(true);
    setVisitorsError('');
    try {
      const data = await getAllSubscribers();
      // Sort newest first
      const sorted = [...data].sort((a, b) => {
        const ta = a.subscribedAt ? new Date(a.subscribedAt).getTime() : 0;
        const tb = b.subscribedAt ? new Date(b.subscribedAt).getTime() : 0;
        return tb - ta;
      });
      setVisitors(sorted);
    } catch (err) {
      console.error('Failed to load visitors:', err);
      setVisitorsError('Failed to load visitor list');
    } finally {
      setVisitorsLoading(false);
    }
  };

  // Auto-load the Visitor List the first time the admin opens that tab.
  useEffect(() => {
    if (activeTab === 'visitors' && visitors.length === 0 && !visitorsLoading) {
      loadVisitors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleChangeVisitorStatus = async (subscriberId, nextStatus) => {
    setSavingId(subscriberId);
    try {
      const updated = await updateSubscriber(subscriberId, { status: nextStatus });
      setVisitors(prev => prev.map(v => (v.id === subscriberId ? { ...v, ...updated } : v)));
    } catch (err) {
      console.error('Failed to update visitor status:', err);
      alert('Failed to update visitor status. Please try again.');
    } finally {
      setSavingId(null);
    }
  };

  const startEditNotes = (subscriber) => {
    setEditingNotesId(subscriber.id);
    setNotesDraft(subscriber.notes || '');
  };

  const cancelEditNotes = () => {
    setEditingNotesId(null);
    setNotesDraft('');
  };

  const saveNotes = async (subscriberId) => {
    setSavingId(subscriberId);
    try {
      const updated = await updateSubscriber(subscriberId, { notes: notesDraft });
      setVisitors(prev => prev.map(v => (v.id === subscriberId ? { ...v, ...updated } : v)));
      setEditingNotesId(null);
      setNotesDraft('');
    } catch (err) {
      console.error('Failed to save notes:', err);
      alert('Failed to save notes. Please try again.');
    } finally {
      setSavingId(null);
    }
  };

  const confirmDeleteVisitor = async () => {
    if (!visitorToDelete) return;
    const id = visitorToDelete.id;
    setDeletingVisitorId(id);
    try {
      await deleteSubscriber(id);
      setVisitors(prev => prev.filter(v => v.id !== id));
      setVisitorToDelete(null);
    } catch (err) {
      console.error('Failed to delete subscriber:', err);
      const msg = err?.response?.data?.detail || 'Failed to delete subscriber. Please try again.';
      alert(msg);
    } finally {
      setDeletingVisitorId(null);
    }
  };

  const filteredVisitors = visitors.filter(v => {
    if (visitorStatusFilter !== 'all' && (v.status || 'new') !== visitorStatusFilter) return false;
    if (!visitorSearch) return true;
    const q = visitorSearch.toLowerCase();
    return [v.firstName, v.email, v.phone, v.city, v.country, v.notes, v.source]
      .filter(Boolean)
      .some(s => String(s).toLowerCase().includes(q));
  });

  // Handle date selection from calendar
  const handleDateSelect = (date) => {
    // Navigate to daily tracker page for the selected date
    const dateStr = format(date, 'yyyy-MM-dd');
    navigate(`/admin/daily/${dateStr}`);
  };

  // Handle quick filters
  const handleQuickFilter = (filterType) => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const tomorrow = format(new Date(Date.now() + 86400000), 'yyyy-MM-dd');

    switch (filterType) {
      case 'today':
        setDateFilter('today');
        setSelectedDate(new Date());
        break;
      case 'tomorrow':
        setDateFilter('tomorrow');
        setSelectedDate(new Date(Date.now() + 86400000));
        break;
      case 'overdue':
        setDateFilter('overdue');
        break;
      case 'all':
        setDateFilter(null);
        break;
      default:
        break;
    }
  };

  // Clear date filter
  const clearDateFilter = () => {
    setDateFilter(null);
  };

  // Filter bookings based on search, status, and date
  const filteredBookings = bookings.filter((booking) => {
    const matchesSearch =
      booking.passengerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.pickupLocation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.dropoffLocation?.toLowerCase().includes(searchTerm.toLowerCase());

    // Map filter values to actual status values
    let matchesStatus = filterStatus === 'all';
    if (!matchesStatus) {
      if (filterStatus === 'confirmed') {
        matchesStatus = booking.status === 'Scheduled';
      } else if (filterStatus === 'completed') {
        matchesStatus = booking.status === 'Completed';
      } else {
        matchesStatus = booking.status === filterStatus;
      }
    }

    // Date filtering logic
    let matchesDate = true;
    if (dateFilter) {
      const bookingDate = booking.date;
      const today = format(new Date(), 'yyyy-MM-dd');
      const tomorrow = format(new Date(Date.now() + 86400000), 'yyyy-MM-dd');

      if (dateFilter === 'today') {
        matchesDate = bookingDate === today;
      } else if (dateFilter === 'tomorrow') {
        matchesDate = bookingDate === tomorrow;
      } else if (dateFilter === 'overdue') {
        matchesDate = bookingDate < today && INCOMPLETE_STATUSES.includes(booking.status);
      } else {
        // Specific date selected from calendar
        matchesDate = bookingDate === dateFilter;
      }
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  // Get label for active filter
  const getActiveFilterLabel = () => {
    if (!dateFilter) return null;
    
    if (dateFilter === 'today') {
      return `Today's Bookings (${format(new Date(), 'MMM d, yyyy')})`;
    } else if (dateFilter === 'tomorrow') {
      return `Tomorrow's Bookings (${format(new Date(Date.now() + 86400000), 'MMM d, yyyy')})`;
    } else if (dateFilter === 'overdue') {
      return 'Overdue Bookings (Incomplete from past dates)';
    } else {
      return `Bookings for ${format(parseISO(dateFilter), 'MMMM d, yyyy')}`;
    }
  };

  // Get bookings for selected date
  const bookingsForDate = filteredBookings.filter(
    (booking) => booking.date === format(selectedDate, 'yyyy-MM-dd')
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled':
        return 'bg-green-500/10 text-green-400 border-green-500/30';
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30';
      case 'Completed':
        return 'bg-blue-500/10 text-blue-400 border-blue-500/30';
      case 'Cancelled':
        return 'bg-red-500/10 text-red-400 border-red-500/30';
      default:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/30';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Scheduled':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'Completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'Cancelled':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const initializeTracking = async (bookingId) => {
    setInitializingTracking(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/tracking/${bookingId}/initialize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (response.ok) {
        alert('Tracking initialized! Customer can now track their ride.');
        setShowTrackingModal(false);
        setSelectedBookingForTracking(null);
      } else {
        const error = await response.json();
        alert(`Failed to initialize tracking: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error initializing tracking:', error);
      alert('Failed to initialize tracking. Please try again.');
    } finally {
      setInitializingTracking(false);
    }
  };

  const deleteBooking = async (bookingId) => {
    setDeleting(true);
    try {
      await deleteBookingAPI(bookingId);
      alert('Booking deleted successfully');
      setShowDeleteModal(false);
      setBookingToDelete(null);
      fetchData(0); // Refresh data
    } catch (error) {
      console.error('Error deleting booking:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Failed to delete booking: ${errorMessage}`);
    } finally {
      setDeleting(false);
    }
  };

  // Sun Med Invoice Handlers
  const handleInvoiceCreate = () => {
    setSelectedInvoice(null);
    setShowInvoiceForm(true);
  };

  const handleInvoiceEdit = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceForm(true);
  };

  const handleInvoiceView = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoicePreview(true);
  };

  const handleInvoiceSend = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoicePreview(true);
  };

  const handleInvoiceFormClose = () => {
    setShowInvoiceForm(false);
    setSelectedInvoice(null);
  };

  const handleInvoicePreviewClose = () => {
    setShowInvoicePreview(false);
    setSelectedInvoice(null);
  };

  const handleInvoiceSaved = () => {
    setInvoiceRefreshKey((prev) => prev + 1);
  };

  const handleInvoiceSent = () => {
    setInvoiceRefreshKey((prev) => prev + 1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-sm text-gray-600 mt-1">Manage bookings, tracking, and reports</p>
              {user && (
                <p className="text-xs text-gray-500 mt-1">Logged in as: {user.email}</p>
              )}
            </div>
            
            {/* Action Toolbar */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              <button
                onClick={() => fetchData(0)}
                disabled={loading}
                className="px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50 hover:border-gray-400 transition flex items-center justify-center gap-2 disabled:opacity-50"
                title="Refresh data"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
              
              <button
                onClick={() => setShowTrackingModal(true)}
                className="px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition flex items-center justify-center gap-2"
              >
                <Navigation className="w-4 h-4" />
                <span>Tracking</span>
              </button>
              
              <button
                onClick={() => navigate('/admin/complete-trip')}
                className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition flex items-center justify-center gap-2"
              >
                <FileText className="w-4 h-4" />
                <span>Receipt</span>
              </button>
              
              <button
                onClick={async () => {
                  await logout();
                  navigate('/');
                }}
                className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition flex items-center justify-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
          
          {/* Secondary Actions */}
          <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-2">
            <button
              onClick={handleExportCSV}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 transition flex items-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Export Bookings</span>
            </button>
            <button
              onClick={handleExportReceiptsCSV}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 transition flex items-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Export Receipts</span>
            </button>
            <button
              onClick={handleExportMailingListCSV}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 transition flex items-center gap-1.5"
            >
              <Mail className="w-3.5 h-3.5" />
              <span>Export Mailing List</span>
            </button>
            <button
              onClick={() => navigate('/admin/reviews')}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 transition flex items-center gap-1.5"
            >
              <Star className="w-3.5 h-3.5" />
              <span>Manage Reviews</span>
            </button>
            <CopyReviewMessageButton />
          </div>
        </div>

        {/* P2: Browser notification permission banner (shown once until dismissed) */}
        {showNotifBanner && (
          <div
            data-testid="notif-permission-banner"
            className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center justify-between gap-3"
          >
            <div className="flex items-center gap-3 min-w-0">
              <Bell className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-blue-900">Get instant alerts for new bookings</p>
                <p className="text-xs text-blue-700 truncate">Allow browser notifications to be alerted the moment a new ride is booked.</p>
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                data-testid="notif-permission-enable-btn"
                onClick={requestNotifPermission}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md transition-colors"
              >
                Enable
              </button>
              <button
                data-testid="notif-permission-dismiss-btn"
                onClick={dismissNotifBanner}
                className="px-3 py-1.5 text-blue-700 hover:bg-blue-100 text-sm rounded-md transition-colors"
                aria-label="Dismiss"
              >
                Not now
              </button>
            </div>
          </div>
        )}

        {/* P2: In-dashboard alerts for new bookings (shown when OS notification cannot fire) */}
        {/* LB-3: Notification sound toggle (off by default, persisted in localStorage) */}
        <div className="mb-3 flex items-center justify-end">
          <label className="inline-flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700">
            <input
              type="checkbox"
              data-testid="admin-notif-sound-toggle"
              checked={soundEnabled}
              onChange={handleToggleSound}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span>🔔 Sound on new booking</span>
            <span
              className={`ml-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                soundEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
              }`}
              data-testid="admin-notif-sound-status"
            >
              {soundEnabled ? 'On' : 'Off'}
            </span>
          </label>
        </div>

        {pendingNewBookings.length > 0 && (
          <div
            data-testid="new-bookings-alert-stack"
            className="mb-4 space-y-2"
          >
            {pendingNewBookings.slice(0, 5).map((b) => (
              <button
                key={b.id}
                data-testid={`new-booking-alert-${b.id}`}
                onClick={() => handleAlertClick(b.id)}
                className="w-full bg-yellow-50 border border-yellow-300 rounded-lg p-3 flex items-center justify-between gap-3 text-left hover:bg-yellow-100 transition-colors shadow-sm"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="relative inline-flex">
                    <Bell className="w-5 h-5 text-yellow-700" />
                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-yellow-50" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-yellow-900 truncate">
                      New booking — {b.passengerName || 'Customer'}
                    </p>
                    <p className="text-xs text-yellow-800 truncate">
                      {(b.pickupZone || '?')} → {(b.dropoffZone || '?')} • {b.date} {b.time}
                    </p>
                  </div>
                </div>
                <span className="text-xs font-semibold text-yellow-800 whitespace-nowrap">View →</span>
              </button>
            ))}
            {pendingNewBookings.length > 0 && (
              <div className="text-right">
                <button
                  data-testid="new-bookings-alert-clear-all"
                  onClick={clearPendingBookings}
                  className="text-xs text-gray-600 hover:text-gray-800 underline"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2 mb-6 flex gap-2">
          <button
            onClick={() => { setActiveTab('bookings'); clearPendingBookings(); }}
            data-testid="admin-tab-bookings"
            className={`flex-1 px-6 py-3 rounded-lg font-semibold transition flex items-center justify-center space-x-2 relative ${
              activeTab === 'bookings'
                ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-black'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <CalendarIcon className="w-5 h-5" />
            <span>Bookings & Tracking</span>
            {pendingNewBookings.length > 0 && (
              <span
                data-testid="bookings-tab-new-badge"
                className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full ring-2 ring-white"
                aria-label={`${pendingNewBookings.length} new bookings`}
              >
                {pendingNewBookings.length > 9 ? '9+' : pendingNewBookings.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('sunmed')}
            className={`flex-1 px-6 py-3 rounded-lg font-semibold transition flex items-center justify-center space-x-2 ${
              activeTab === 'sunmed'
                ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Receipt className="w-5 h-5" />
            <span>Sun Med Billing</span>
          </button>
          <button
            onClick={() => setActiveTab('visitors')}
            data-testid="admin-tab-visitors"
            className={`flex-1 px-6 py-3 rounded-lg font-semibold transition flex items-center justify-center space-x-2 ${
              activeTab === 'visitors'
                ? 'bg-gradient-to-r from-coral-500 to-pink-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Users className="w-5 h-5" />
            <span>Visitor List</span>
          </button>
          <button
            onClick={() => setActiveTab('blog')}
            data-testid="admin-tab-blog"
            className={`flex-1 px-6 py-3 rounded-lg font-semibold transition flex items-center justify-center space-x-2 ${
              activeTab === 'blog'
                ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <FileText className="w-5 h-5" />
            <span>Blog / Hey Taxi!</span>
          </button>
        </div>

        {/* Bookings Content */}
        {activeTab === 'bookings' && (
          <>
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 backdrop-blur-sm p-6 rounded-2xl border border-yellow-500/30">
            <div className="text-yellow-400 text-3xl font-bold mb-2">
              {bookings.length}
            </div>
            <div className="text-gray-400 text-sm">Total Bookings</div>
          </div>

          <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 backdrop-blur-sm p-6 rounded-2xl border border-green-500/30">
            <div className="text-green-400 text-3xl font-bold mb-2">
              {(() => {
                const today = new Date().toISOString().split('T')[0];
                return bookings.filter((b) => 
                  INCOMPLETE_STATUSES.includes(b.status) && b.date >= today
                ).length;
              })()}
            </div>
            <div className="text-gray-400 text-sm">Upcoming Scheduled</div>
          </div>

          <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 backdrop-blur-sm p-6 rounded-2xl border border-blue-500/30">
            <div className="text-blue-400 text-3xl font-bold mb-2">
              {bookings.filter((b) => b.status === 'Completed').length}
            </div>
            <div className="text-gray-400 text-sm">Completed</div>
          </div>

          <div className="bg-gradient-to-br from-red-500/10 to-red-600/5 backdrop-blur-sm p-6 rounded-2xl border border-red-500/30">
            <div className="text-red-400 text-3xl font-bold mb-2">
              {(() => {
                const today = new Date().toISOString().split('T')[0];
                return bookings.filter((b) => 
                  INCOMPLETE_STATUSES.includes(b.status) && b.date < today
                ).length;
              })()}
            </div>
            <div className="text-gray-400 text-sm">Overdue</div>
          </div>
        </div>

        {/* Upcoming Rides Widget */}
        <div className="mb-12">
          <UpcomingRidesWidget />
        </div>

        {/* Search and Filter */}
        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm p-6 rounded-2xl border border-yellow-500/20 mb-8">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, ID, or location..."
                className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all duration-300"
              />
            </div>

            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="pl-12 pr-8 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all duration-300 appearance-none"
              >
                <option value="all">All Status</option>
                <option value="confirmed">Scheduled</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* Quick Date Filters */}
          <div>
            <p className="text-gray-400 text-sm font-semibold mb-3">Quick Date Filters:</p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => handleQuickFilter('today')}
                className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 ${
                  dateFilter === 'today'
                    ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-black'
                    : 'bg-gray-800/50 text-gray-400 border border-gray-700 hover:border-yellow-500/50'
                }`}
              >
                <Clock className="w-4 h-4 inline-block mr-1" />
                Today
              </button>
              <button
                onClick={() => handleQuickFilter('tomorrow')}
                className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 ${
                  dateFilter === 'tomorrow'
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
                    : 'bg-gray-800/50 text-gray-400 border border-gray-700 hover:border-blue-500/50'
                }`}
              >
                <CalendarIcon className="w-4 h-4 inline-block mr-1" />
                Tomorrow
              </button>
              <button
                onClick={() => handleQuickFilter('overdue')}
                className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 ${
                  dateFilter === 'overdue'
                    ? 'bg-gradient-to-r from-red-500 to-red-600 text-white'
                    : 'bg-gray-800/50 text-gray-400 border border-gray-700 hover:border-red-500/50'
                }`}
              >
                <AlertTriangle className="w-4 h-4 inline-block mr-1" />
                Overdue
              </button>
              <button
                onClick={() => handleQuickFilter('all')}
                className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 ${
                  !dateFilter
                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white'
                    : 'bg-gray-800/50 text-gray-400 border border-gray-700 hover:border-green-500/50'
                }`}
              >
                All Bookings
              </button>
            </div>
          </div>

          {/* Active Filter Display */}
          {dateFilter && (
            <div className="mt-4 pt-4 border-t border-yellow-500/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Filter className="w-4 h-4 text-yellow-400" />
                  <p className="text-yellow-400 font-semibold text-sm">
                    {getActiveFilterLabel()}
                  </p>
                </div>
                <button
                  onClick={clearDateFilter}
                  className="flex items-center space-x-1 text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                  <span className="text-sm">Clear Filter</span>
                </button>
              </div>
              <p className="text-gray-500 text-xs mt-2">
                Showing {filteredBookings.length} booking{filteredBookings.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Enhanced Calendar with Booking Status */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm p-6 rounded-2xl border border-yellow-500/20 sticky top-32">
              <div className="flex items-center space-x-3 mb-6">
                <CalendarIcon className="w-6 h-6 text-yellow-400" />
                <h2 className="text-2xl font-bold text-white">Calendar</h2>
              </div>
              <BookingCalendar
                bookings={bookings}
                selectedDate={selectedDate}
                onSelectDate={handleDateSelect}
              />
            </div>
          </div>

          {/* Bookings List */}
          <div className="lg:col-span-2">
            {loading ? (
              <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm p-12 rounded-2xl border border-yellow-500/20 text-center">
                <p className="text-gray-400 text-lg">Loading bookings...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredBookings.length === 0 ? (
                  <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm p-12 rounded-2xl border border-yellow-500/20 text-center">
                    <p className="text-gray-400 text-lg">No bookings found</p>
                  </div>
                ) : (
                filteredBookings.map((booking) => (
                  <div
                    key={booking.id}
                    data-testid={`booking-row-${booking.id}`}
                    className={`bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm p-6 rounded-2xl border transition-all duration-300 ${
                      highlightedBookingId === booking.id
                        ? 'border-yellow-400 ring-2 ring-yellow-400/60 shadow-[0_0_24px_rgba(250,204,21,0.45)]'
                        : 'border-yellow-500/20 hover:border-yellow-500/50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-white mb-1">
                          {booking.passengerName}
                        </h3>
                        <p className="text-gray-400 text-sm">ID: {booking.id}</p>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold border flex items-center space-x-1 ${getStatusColor(
                          booking.status
                        )}`}
                      >
                        {getStatusIcon(booking.status)}
                        <span className="capitalize">{booking.status}</span>
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-gray-400 text-sm mb-1">Pickup</p>
                        <p className="text-white">{booking.pickupLocation}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm mb-1">Drop-off</p>
                        <p className="text-white">{booking.dropoffLocation}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-yellow-500/20">
                      <div>
                        <p className="text-gray-400 text-xs mb-1">Date</p>
                        <p className="text-white text-sm font-semibold">{booking.date}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs mb-1">Time</p>
                        <p className="text-white text-sm font-semibold">{formatTime12Hour(booking.time)}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs mb-1">Email</p>
                        <p className="text-white text-sm font-semibold truncate">
                          {booking.passengerEmail}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs mb-1">Phone</p>
                        <p className="text-white text-sm font-semibold">
                          {booking.passengerPhone}
                        </p>
                      </div>
                    </div>

                    {booking.specialRequests && (
                      <div className="mt-4 pt-4 border-t border-yellow-500/20">
                        <p className="text-gray-400 text-xs mb-1">Special Requests</p>
                        <p className="text-white text-sm">{booking.specialRequests}</p>
                      </div>
                    )}

                    {/* Flight Details */}
                    {(booking.airline || booking.flightNumber || booking.departureTime || booking.arrivalTime) && (
                      <div className="mt-4 pt-4 border-t border-yellow-500/20">
                        <p className="text-gray-400 text-xs mb-2">Flight Details</p>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {booking.airline && (
                            <div>
                              <span className="text-gray-500">Airline:</span>{' '}
                              <span className="text-white font-semibold">{booking.airline}</span>
                            </div>
                          )}
                          {booking.flightNumber && (
                            <div>
                              <span className="text-gray-500">Flight #:</span>{' '}
                              <span className="text-white font-semibold">{booking.flightNumber}</span>
                            </div>
                          )}
                          {booking.departureTime && (
                            <div>
                              <span className="text-gray-500">Departure:</span>{' '}
                              <span className="text-white font-semibold">{booking.departureTime}</span>
                            </div>
                          )}
                          {booking.arrivalTime && (
                            <div>
                              <span className="text-gray-500">Arrival:</span>{' '}
                              <span className="text-white font-semibold">{booking.arrivalTime}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Email Tracking Status */}
                    <div className="mt-4 pt-4 border-t border-yellow-500/20">
                      <p className="text-gray-400 text-xs mb-3">Email Delivery Status</p>
                      <div className="space-y-2">
                        {/* Confirmation Email Status */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Mail className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-white text-sm">Confirmation Email:</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            {booking.confirmationEmailStatus === 'sent' && (
                              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded border border-green-500/30 flex items-center space-x-1">
                                <CheckCircle className="w-3 h-3" />
                                <span>Sent</span>
                              </span>
                            )}
                            {booking.confirmationEmailStatus === 'failed' && (
                              <>
                                <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded border border-red-500/30 flex items-center space-x-1">
                                  <XCircle className="w-3 h-3" />
                                  <span>Failed</span>
                                </span>
                                <button
                                  onClick={async () => {
                                    try {
                                      const res = await resendConfirmationEmail(booking.id, 'customer');
                                      alert(`Customer confirmation: ${res.customerStatus}` + (res.customerFailureReason ? `\n${res.customerFailureReason}` : ''));
                                      fetchData();
                                    } catch (error) {
                                      alert(`Failed to resend: ${error.response?.data?.detail || error.message}`);
                                    }
                                  }}
                                  className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded hover:bg-yellow-500/30 border border-yellow-500/30 transition"
                                  data-testid={`admin-resend-customer-row-${booking.id}`}
                                  title="Resend customer confirmation only"
                                >
                                  <RefreshCw className="w-3 h-3" />
                                </button>
                              </>
                            )}
                            {booking.confirmationEmailStatus === 'pending' && (
                              <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded border border-yellow-500/30 flex items-center space-x-1">
                                <Clock className="w-3 h-3" />
                                <span>Pending</span>
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Admin Alert Email Status — visible on the booking row so a failed
                            owner notification is noticed immediately without opening details.
                            PRODUCTION-CRITICAL TAXI-98 BOOKING NOTIFICATION LOGIC. */}
                        {(booking.adminEmailStatus === 'failed' || booking.adminEmailStatus === 'pending') && (
                          <div className="flex items-center justify-between" data-testid={`admin-alert-row-${booking.id}`}>
                            <div className="flex items-center space-x-2">
                              <Mail className="w-3.5 h-3.5 text-gray-400" />
                              <span className="text-white text-sm">Admin Alert:</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              {booking.adminEmailStatus === 'failed' && (
                                <>
                                  <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded border border-red-500/30 flex items-center space-x-1">
                                    <XCircle className="w-3 h-3" />
                                    <span>Failed</span>
                                  </span>
                                  <button
                                    onClick={async () => {
                                      try {
                                        const res = await resendConfirmationEmail(booking.id, 'admin');
                                        alert(`Admin notification: ${res.adminStatus}` + (res.adminFailureReason ? `\n${res.adminFailureReason}` : ''));
                                        fetchData();
                                      } catch (error) {
                                        alert(`Failed to resend: ${error.response?.data?.detail || error.message}`);
                                      }
                                    }}
                                    className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded hover:bg-yellow-500/30 border border-yellow-500/30 transition"
                                    data-testid={`admin-resend-admin-row-${booking.id}`}
                                    title="Resend admin notification only"
                                  >
                                    <RefreshCw className="w-3 h-3" />
                                  </button>
                                </>
                              )}
                              {booking.adminEmailStatus === 'pending' && (
                                <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded border border-yellow-500/30 flex items-center space-x-1">
                                  <Clock className="w-3 h-3" />
                                  <span>Pending</span>
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Receipt Email Status (only show if booking is completed) */}
                        {booking.status === 'Completed' && (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <FileText className="w-3.5 h-3.5 text-gray-400" />
                              <span className="text-white text-sm">Receipt Email:</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              {booking.receiptEmailStatus === 'sent' && (
                                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded border border-green-500/30 flex items-center space-x-1">
                                  <CheckCircle className="w-3 h-3" />
                                  <span>Sent</span>
                                </span>
                              )}
                              {booking.receiptEmailStatus === 'failed' && (
                                <>
                                  <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded border border-red-500/30 flex items-center space-x-1">
                                    <XCircle className="w-3 h-3" />
                                    <span>Failed</span>
                                  </span>
                                  <button
                                    onClick={async () => {
                                      try {
                                        await resendReceiptEmail(booking.id);
                                        alert('Receipt email resent successfully!');
                                        fetchData();
                                      } catch (error) {
                                        alert(`Failed to resend: ${error.response?.data?.detail || error.message}`);
                                      }
                                    }}
                                    className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded hover:bg-yellow-500/30 border border-yellow-500/30 transition"
                                  >
                                    <RefreshCw className="w-3 h-3" />
                                  </button>
                                </>
                              )}
                              {booking.receiptEmailStatus === 'pending' && (
                                <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded border border-yellow-500/30 flex items-center space-x-1">
                                  <Clock className="w-3 h-3" />
                                  <span>Pending</span>
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Email Failure Reason */}
                        {booking.emailFailureReason && (
                          <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded text-xs">
                            <p className="text-red-400">
                              <strong>Failure Reason:</strong> {booking.emailFailureReason}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-yellow-500/20 flex justify-between items-center">
                      <button
                        onClick={() => {
                          setSelectedBookingDetails(booking);
                          setShowDetailsModal(true);
                        }}
                        className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 border border-blue-500/30 transition text-sm font-semibold flex items-center space-x-2"
                      >
                        <FileText className="w-4 h-4" />
                        <span>View Full Details</span>
                      </button>
                      
                      <button
                        onClick={() => {
                          setBookingToDelete(booking);
                          setShowDeleteModal(true);
                        }}
                        className="px-3 py-1.5 bg-red-600/20 text-red-400 text-xs rounded hover:bg-red-600/30 border border-red-600/30 transition flex items-center gap-1.5"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                ))
              )}
              </div>
            )}
          </div>
        </div>
        </>
        )}

        {/* Sun Med Billing Content */}
        {activeTab === 'sunmed' && (
          <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm p-8 rounded-2xl border border-cyan-500/20">
            <SunMedInvoiceList
              key={invoiceRefreshKey}
              onCreateNew={handleInvoiceCreate}
              onEdit={handleInvoiceEdit}
              onView={handleInvoiceView}
              onSend={handleInvoiceSend}
            />
          </div>
        )}

        {/* Visitor List Content — 2026-06-06 */}
        {activeTab === 'visitors' && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8" data-testid="admin-visitor-list-panel">
            {/* Header + toolbar */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Users className="w-6 h-6 text-coral-600" />
                  <span>Visitor List</span>
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Every visitor list signup from the public site. Update status and notes inline; an alert email is sent to stephen@taxi-98.com on every new signup.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={loadVisitors}
                  data-testid="admin-visitors-refresh-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold transition"
                >
                  <RefreshCw className={`w-4 h-4 ${visitorsLoading ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </button>
                <button
                  onClick={handleExportMailingListCSV}
                  data-testid="admin-visitors-csv-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-coral-500 to-pink-500 hover:from-coral-600 hover:to-pink-600 text-white rounded-lg text-sm font-semibold transition shadow"
                >
                  <Download className="w-4 h-4" />
                  <span>Export CSV</span>
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, phone, city, country, notes…"
                  value={visitorSearch}
                  onChange={(e) => setVisitorSearch(e.target.value)}
                  data-testid="admin-visitors-search"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-400/40 focus:border-coral-400 text-sm"
                />
              </div>
              <select
                value={visitorStatusFilter}
                onChange={(e) => setVisitorStatusFilter(e.target.value)}
                data-testid="admin-visitors-status-filter"
                className="px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-400/40 focus:border-coral-400 text-sm bg-white"
              >
                <option value="all">All Statuses</option>
                {VISITOR_STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* States */}
            {visitorsLoading && (
              <div className="py-12 text-center text-gray-500 text-sm" data-testid="admin-visitors-loading">
                Loading visitor list…
              </div>
            )}
            {!visitorsLoading && visitorsError && (
              <div className="py-6 px-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm" data-testid="admin-visitors-error">
                {visitorsError}
              </div>
            )}
            {!visitorsLoading && !visitorsError && filteredVisitors.length === 0 && (
              <div className="py-12 text-center text-gray-500 text-sm" data-testid="admin-visitors-empty">
                No visitor records match the current filter.
              </div>
            )}

            {/* Table */}
            {!visitorsLoading && !visitorsError && filteredVisitors.length > 0 && (
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="min-w-full text-sm" data-testid="admin-visitors-table">
                  <thead className="bg-gray-50 text-gray-600 uppercase text-[11px] tracking-wider">
                    <tr>
                      <th className="px-3 py-3 text-left">Name</th>
                      <th className="px-3 py-3 text-left">Email</th>
                      <th className="px-3 py-3 text-left">Phone / WhatsApp</th>
                      <th className="px-3 py-3 text-left">City</th>
                      <th className="px-3 py-3 text-left">Country</th>
                      <th className="px-3 py-3 text-left">Source</th>
                      <th className="px-3 py-3 text-left min-w-[220px]">Notes</th>
                      <th className="px-3 py-3 text-left">Submitted</th>
                      <th className="px-3 py-3 text-left">Status</th>
                      <th className="px-3 py-3 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVisitors.map((v) => {
                      const isEditingNotes = editingNotesId === v.id;
                      const submitted = v.subscribedAt ? new Date(v.subscribedAt) : null;
                      const submittedLabel = submitted ? submitted.toLocaleString() : '—';
                      return (
                        <tr key={v.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`admin-visitor-row-${v.id}`}>
                          <td className="px-3 py-3 font-medium text-gray-900">{v.firstName || '—'}</td>
                          <td className="px-3 py-3 text-gray-700 break-all">
                            <a href={`mailto:${v.email}`} className="text-coral-700 hover:text-coral-800 underline-offset-2 hover:underline">{v.email}</a>
                          </td>
                          <td className="px-3 py-3 text-gray-700">
                            {v.phone ? (
                              <a href={`tel:${v.phone}`} className="text-gray-800 hover:text-coral-700">{v.phone}</a>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-gray-700">{v.city || <span className="text-gray-400">—</span>}</td>
                          <td className="px-3 py-3 text-gray-700">{v.country || <span className="text-gray-400">—</span>}</td>
                          <td className="px-3 py-3 text-gray-500 text-xs">{v.source || 'homepage-mailing-list-signup'}</td>
                          <td className="px-3 py-3 text-gray-700">
                            {isEditingNotes ? (
                              <div className="flex flex-col gap-2">
                                <textarea
                                  value={notesDraft}
                                  onChange={(e) => setNotesDraft(e.target.value)}
                                  rows={3}
                                  data-testid={`admin-visitor-notes-input-${v.id}`}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-coral-400/40 focus:border-coral-400 resize-none"
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => saveNotes(v.id)}
                                    disabled={savingId === v.id}
                                    data-testid={`admin-visitor-notes-save-${v.id}`}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-coral-600 hover:bg-coral-700 text-white text-xs font-semibold rounded-md disabled:opacity-50"
                                  >
                                    <Save className="w-3.5 h-3.5" />
                                    Save
                                  </button>
                                  <button
                                    onClick={cancelEditNotes}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-md"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => startEditNotes(v)}
                                data-testid={`admin-visitor-notes-edit-${v.id}`}
                                title="Click to edit notes"
                                className="text-left w-full whitespace-pre-wrap text-gray-700 hover:bg-coral-50 hover:text-coral-800 rounded px-2 py-1 -mx-2 transition"
                              >
                                {v.notes ? v.notes : <span className="text-gray-400 italic">Click to add notes…</span>}
                              </button>
                            )}
                          </td>
                          <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{submittedLabel}</td>
                          <td className="px-3 py-3">
                            <div className="flex flex-col items-start gap-1.5">
                              <span className={`inline-block px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider rounded-full border ${visitorStatusBadgeClass(v.status)}`} data-testid={`admin-visitor-status-badge-${v.id}`}>
                                {formatVisitorStatusLabel(v.status)}
                              </span>
                              <select
                                value={(v.status || 'new').toLowerCase()}
                                onChange={(e) => handleChangeVisitorStatus(v.id, e.target.value)}
                                disabled={savingId === v.id}
                                data-testid={`admin-visitor-status-select-${v.id}`}
                                className="text-xs px-2 py-1 border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-coral-400/40 disabled:opacity-50"
                              >
                                {VISITOR_STATUS_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <button
                              type="button"
                              onClick={() => setVisitorToDelete(v)}
                              disabled={deletingVisitorId === v.id}
                              data-testid={`admin-visitor-delete-${v.id}`}
                              title="Delete this subscriber"
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold text-red-700 hover:text-white hover:bg-red-600 border border-red-200 hover:border-red-600 rounded-md transition disabled:opacity-50"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Blog / Hey Taxi! Content — 2026-06-10 */}
        {activeTab === 'blog' && <AdminBlogPanel />}

      </div>

      {/* Sun Med Invoice Modals */}
      {showInvoiceForm && (
        <SunMedInvoiceForm
          invoice={selectedInvoice}
          onClose={handleInvoiceFormClose}
          onSave={handleInvoiceSaved}
        />
      )}

      {showInvoicePreview && selectedInvoice && (
        <SunMedInvoicePreview
          invoice={selectedInvoice}
          onClose={handleInvoicePreviewClose}
          onSent={handleInvoiceSent}
        />
      )}

      {/* Tracking Modal */}
      {showTrackingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-8 max-w-md w-full">
            <h3 className="text-2xl font-bold mb-4">Initialize Tracking</h3>
            <p className="text-gray-600 mb-6">
              Select a booking to start GPS tracking. This will geocode the addresses and enable live tracking for the customer.
            </p>
            <select
              value={selectedBookingForTracking || ''}
              onChange={(e) => setSelectedBookingForTracking(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-6 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            >
              <option value="">Select a booking...</option>
              {bookings
                .filter(b => b.status !== 'Completed' && b.status !== 'Cancelled')
                .map((booking) => (
                  <option key={booking.id} value={booking.id}>
                    {booking.customerName} - {booking.pickupLocation} → {booking.dropoffLocation}
                  </option>
                ))}
            </select>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowTrackingModal(false);
                  setSelectedBookingForTracking(null);
                }}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-bold hover:bg-gray-300"
                disabled={initializingTracking}
              >
                Cancel
              </button>
              <button
                onClick={() => initializeTracking(selectedBookingForTracking)}
                disabled={!selectedBookingForTracking || initializingTracking}
                className="flex-1 px-6 py-3 bg-green-500 text-white rounded-lg font-bold hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {initializingTracking ? 'Initializing...' : 'Start Tracking'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Visitor/Subscriber Confirmation Modal */}
      {visitorToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="admin-visitor-delete-modal">
          <div className="bg-white rounded-xl p-8 max-w-md w-full">
            <h3 className="text-2xl font-bold mb-4 text-red-600">Delete Subscriber</h3>
            <p className="text-gray-600 mb-2">
              Are you sure you want to permanently delete this visitor list subscriber?
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-gray-700"><strong>Name:</strong> {visitorToDelete.firstName || '—'}</p>
              <p className="text-sm text-gray-700 break-all"><strong>Email:</strong> {visitorToDelete.email}</p>
              {visitorToDelete.phone && (
                <p className="text-sm text-gray-700"><strong>Phone:</strong> {visitorToDelete.phone}</p>
              )}
              {visitorToDelete.source && (
                <p className="text-xs text-gray-500 mt-1"><strong>Source:</strong> {visitorToDelete.source}</p>
              )}
            </div>
            <p className="text-sm text-red-600 mb-6 font-semibold">
              ⚠️ This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setVisitorToDelete(null)}
                disabled={deletingVisitorId === visitorToDelete.id}
                data-testid="admin-visitor-delete-cancel"
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-bold hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteVisitor}
                disabled={deletingVisitorId === visitorToDelete.id}
                data-testid="admin-visitor-delete-confirm"
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingVisitorId === visitorToDelete.id ? 'Deleting...' : 'Delete Subscriber'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && bookingToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-8 max-w-md w-full">
            <h3 className="text-2xl font-bold mb-4 text-red-600">Delete Booking</h3>
            <p className="text-gray-600 mb-2">
              Are you sure you want to delete this booking?
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-gray-700"><strong>Customer:</strong> {bookingToDelete.passengerName}</p>
              <p className="text-sm text-gray-700"><strong>Route:</strong> {bookingToDelete.pickupLocation} → {bookingToDelete.dropoffLocation}</p>
              <p className="text-sm text-gray-700"><strong>Date:</strong> {bookingToDelete.date} at {bookingToDelete.time}</p>
            </div>
            <p className="text-sm text-red-600 mb-6 font-semibold">
              ⚠️ This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setBookingToDelete(null);
                }}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-bold hover:bg-gray-300"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={() => deleteBooking(bookingToDelete.id)}
                disabled={deleting}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? 'Deleting...' : 'Delete Booking'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full Booking Details Modal */}
      {showDetailsModal && selectedBookingDetails && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 max-w-4xl w-full my-8 border-2 border-yellow-500/30 shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-start justify-between mb-6 sticky top-0 bg-gradient-to-br from-gray-900 to-gray-800 pb-4 z-10">
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">Complete Booking Details</h2>
                <p className="text-gray-400">Booking ID: {selectedBookingDetails.id}</p>
              </div>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedBookingDetails(null);
                }}
                className="text-gray-400 hover:text-white transition"
              >
                <XCircle className="w-8 h-8" />
              </button>
            </div>

            {/* Status Badge */}
            <div className="mb-6">
              <span className={`px-4 py-2 rounded-full text-sm font-bold border inline-flex items-center space-x-2 ${getStatusColor(selectedBookingDetails.status)}`}>
                {getStatusIcon(selectedBookingDetails.status)}
                <span className="capitalize">{selectedBookingDetails.status}</span>
              </span>
            </div>

            {/* Full Details Grid */}
            <div className="space-y-6">

              {/* Email Delivery Status — LB-3 */}
              <BookingEmailStatus bookingId={selectedBookingDetails.id} />

              {/* Customer Information */}
              <div className="bg-gray-800/50 p-6 rounded-xl border border-yellow-500/20">
                <h3 className="text-xl font-bold text-yellow-400 mb-4 flex items-center space-x-2">
                  <User className="w-5 h-5" />
                  <span>Customer Information</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Full Name</p>
                    <p className="text-white font-semibold text-lg">{selectedBookingDetails.passengerName}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Email Address</p>
                    <p className="text-white font-semibold break-all">{selectedBookingDetails.passengerEmail}</p>
                    <a href={`mailto:${selectedBookingDetails.passengerEmail}`} className="text-blue-400 hover:underline text-sm">Send Email</a>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Phone Number</p>
                    <p className="text-white font-semibold">{selectedBookingDetails.passengerPhone}</p>
                    <a href={`tel:${selectedBookingDetails.passengerPhone}`} className="text-blue-400 hover:underline text-sm">Call Now</a>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Booking Created</p>
                    <p className="text-white font-semibold">{selectedBookingDetails.createdAt ? new Date(selectedBookingDetails.createdAt).toLocaleString() : 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Trip Details */}
              <div className="bg-gray-800/50 p-6 rounded-xl border border-yellow-500/20">
                <h3 className="text-xl font-bold text-yellow-400 mb-4 flex items-center space-x-2">
                  <MapPin className="w-5 h-5" />
                  <span>Trip Details</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Pickup Date</p>
                    <p className="text-white font-semibold text-lg">{selectedBookingDetails.date}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Pickup Time</p>
                    <p className="text-white font-semibold text-lg">{formatTime12Hour(selectedBookingDetails.time)}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-gray-400 text-sm mb-1">Pickup Location</p>
                    <p className="text-white font-semibold">{selectedBookingDetails.pickupLocation}</p>
                    <p className="text-gray-500 text-sm">Zone: {selectedBookingDetails.pickupZone}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-gray-400 text-sm mb-1">Dropoff Location</p>
                    <p className="text-white font-semibold">{selectedBookingDetails.dropoffLocation}</p>
                    <p className="text-gray-500 text-sm">Zone: {selectedBookingDetails.dropoffZone}</p>
                  </div>
                </div>
              </div>

              {/* Service Details */}
              <div className="bg-gray-800/50 p-6 rounded-xl border border-yellow-500/20">
                <h3 className="text-xl font-bold text-yellow-400 mb-4 flex items-center space-x-2">
                  <Car className="w-5 h-5" />
                  <span>Service Details</span>
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Service Type</p>
                    <p className="text-white font-semibold capitalize">{selectedBookingDetails.serviceType || 'Standard'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Passengers</p>
                    <p className="text-white font-semibold">{selectedBookingDetails.passengers}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Luggage</p>
                    <p className="text-white font-semibold">{selectedBookingDetails.luggage}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Large Bags</p>
                    <p className="text-white font-semibold">{selectedBookingDetails.largeBags || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Children Under 3</p>
                    <p className="text-white font-semibold">{selectedBookingDetails.childrenUnder3 || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Car Seats</p>
                    <p className="text-white font-semibold">{selectedBookingDetails.carSeats || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Waiting Time</p>
                    <p className="text-white font-semibold">{selectedBookingDetails.waitingMinutes || 0} min</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Extra Bags</p>
                    <p className="text-white font-semibold">{selectedBookingDetails.bags ? 'Yes' : 'No'}</p>
                  </div>
                </div>

                {/* Shopping Plaza Stop — label only; price is billed via waiting time (2026-07-11) */}
                {(() => {
                  const sStop = (selectedBookingDetails.shoppingStop || 'none').toLowerCase();
                  const labelMap = {
                    grocery: 'Grocery Store Stop',
                    liquor:  'Liquor Store Stop',
                    both:    'Grocery + Liquor Store Stop',
                  };
                  if (!labelMap[sStop]) return null;
                  return (
                    <div className="mt-4 p-4 rounded-lg bg-cyan-900/20 border border-cyan-500/30" data-testid="admin-shopping-stop-display">
                      <p className="text-cyan-300 text-sm mb-1">Shopping Plaza Stop</p>
                      <p className="text-white font-semibold">{labelMap[sStop]}</p>
                      <p className="text-cyan-200 text-xs mt-1 italic">Charged by waiting time</p>
                    </div>
                  );
                })()}
              </div>

              {/* Flight Details */}
              {(selectedBookingDetails.airline || selectedBookingDetails.flightNumber || selectedBookingDetails.arrivalTime || selectedBookingDetails.departureTime) && (
                <div className="bg-gray-800/50 p-6 rounded-xl border border-yellow-500/20">
                  <h3 className="text-xl font-bold text-yellow-400 mb-4 flex items-center space-x-2">
                    <Plane className="w-5 h-5" />
                    <span>Flight Information</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedBookingDetails.airline && (
                      <div>
                        <p className="text-gray-400 text-sm mb-1">Airline</p>
                        <p className="text-white font-semibold">{selectedBookingDetails.airline}</p>
                      </div>
                    )}
                    {selectedBookingDetails.flightNumber && (
                      <div>
                        <p className="text-gray-400 text-sm mb-1">Flight Number</p>
                        <p className="text-white font-semibold">{selectedBookingDetails.flightNumber}</p>
                      </div>
                    )}
                    {selectedBookingDetails.departureTime && (
                      <div>
                        <p className="text-gray-400 text-sm mb-1">Departure Time</p>
                        <p className="text-white font-semibold">{selectedBookingDetails.departureTime}</p>
                      </div>
                    )}
                    {selectedBookingDetails.arrivalTime && (
                      <div>
                        <p className="text-gray-400 text-sm mb-1">Arrival Time</p>
                        <p className="text-white font-semibold">{selectedBookingDetails.arrivalTime}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Payment & Pricing */}
              <div className="bg-gray-800/50 p-6 rounded-xl border border-yellow-500/20">
                <h3 className="text-xl font-bold text-yellow-400 mb-4 flex items-center space-x-2">
                  <DollarSign className="w-5 h-5" />
                  <span>Payment Information</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Payment Method</p>
                    <p className="text-white font-semibold capitalize">{selectedBookingDetails.paymentMethod || 'Cash'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Estimated Fare</p>
                    <p className="text-white font-semibold">${selectedBookingDetails.estimatedFare || 0} USD</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Actual Fare</p>
                    {editingFare ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          step="0.01"
                          value={newActualFare}
                          onChange={(e) => setNewActualFare(e.target.value)}
                          className="w-24 px-2 py-1 bg-gray-700 border border-yellow-500/30 rounded text-white"
                          placeholder="0.00"
                        />
                        <button
                          onClick={async () => {
                            try {
                              const fare = parseFloat(newActualFare);
                              if (isNaN(fare) || fare < 0) {
                                alert('Please enter a valid fare amount');
                                return;
                              }
                              await updateBookingFare(selectedBookingDetails.id, fare);
                              alert('Fare updated successfully!');
                              setEditingFare(false);
                              fetchData();
                              // Update the modal data
                              setSelectedBookingDetails({
                                ...selectedBookingDetails,
                                actualFare: fare,
                                finalFare: fare
                              });
                            } catch (error) {
                              alert(`Failed to update fare: ${error.response?.data?.detail || error.message}`);
                            }
                          }}
                          className="px-2 py-1 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 text-xs"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingFare(false);
                            setNewActualFare('');
                          }}
                          className="px-2 py-1 bg-gray-600 text-white rounded hover:bg-gray-500 text-xs"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <p className="text-white font-semibold">${selectedBookingDetails.actualFare || selectedBookingDetails.estimatedFare || 0} USD</p>
                        <button
                          onClick={() => {
                            setEditingFare(true);
                            setNewActualFare((selectedBookingDetails.actualFare || selectedBookingDetails.estimatedFare || 0).toString());
                          }}
                          className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30 text-xs border border-blue-500/30"
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Special Requests */}
              {selectedBookingDetails.specialRequests && (
                <div className="bg-gray-800/50 p-6 rounded-xl border border-yellow-500/20">
                  <h3 className="text-xl font-bold text-yellow-400 mb-4 flex items-center space-x-2">
                    <MessageSquare className="w-5 h-5" />
                    <span>Special Requests / Notes</span>
                  </h3>
                  <p className="text-white">{selectedBookingDetails.specialRequests}</p>
                </div>
              )}

              {/* Tracking & Status */}
              <div className="bg-gray-800/50 p-6 rounded-xl border border-yellow-500/20">
                <h3 className="text-xl font-bold text-yellow-400 mb-4 flex items-center space-x-2">
                  <Navigation className="w-5 h-5" />
                  <span>Tracking & System Info</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Tracking Link</p>
                    <p className="text-blue-400 hover:underline cursor-pointer text-sm break-all">
                      {selectedBookingDetails.trackingLink || 'Not available'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Booking Source</p>
                    <p className="text-white font-semibold capitalize">{selectedBookingDetails.bookingSource || 'Website'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Confirmation Email</p>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${selectedBookingDetails.confirmationEmailStatus === 'sent' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : selectedBookingDetails.confirmationEmailStatus === 'failed' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'}`} data-testid="admin-customer-email-status">
                        {selectedBookingDetails.confirmationEmailStatus || 'pending'}
                      </span>
                      {typeof selectedBookingDetails.customerSendGridResponseCode === 'number' && (
                        <span className="text-gray-500 text-xs" title="SendGrid HTTP status">SG {selectedBookingDetails.customerSendGridResponseCode}</span>
                      )}
                      {(selectedBookingDetails.confirmationEmailStatus === 'failed' || selectedBookingDetails.confirmationEmailStatus === 'pending') && (
                        <button
                          onClick={async () => {
                            try {
                              const res = await resendConfirmationEmail(selectedBookingDetails.id, 'customer');
                              alert(`Customer confirmation: ${res.customerStatus}` + (res.customerFailureReason ? `\n${res.customerFailureReason}` : (typeof res.customerSendGridResponseCode === 'number' ? `\nSG ${res.customerSendGridResponseCode}` : '')));
                              fetchData();
                            } catch (error) {
                              alert(`Failed to resend: ${error.response?.data?.detail || error.message}`);
                            }
                          }}
                          className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30 text-xs border border-blue-500/30"
                          data-testid="admin-resend-customer-btn"
                          title="Resend customer confirmation only"
                        >
                          <RefreshCw className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    {selectedBookingDetails.confirmationEmailSentAt && (
                      <p className="text-gray-500 text-xs mt-1">
                        Sent: {new Date(selectedBookingDetails.confirmationEmailSentAt).toLocaleString()}
                      </p>
                    )}
                    {selectedBookingDetails.confirmationEmailStatus === 'failed' && selectedBookingDetails.emailFailureReason && (
                      <p className="text-red-300 text-xs mt-1 break-all" data-testid="admin-email-failure-reason" title={selectedBookingDetails.emailFailureReason}>
                        ⚠ {selectedBookingDetails.emailFailureReason.slice(0, 140)}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Admin Alert (stephen@ / bookings@)</p>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${selectedBookingDetails.adminEmailStatus === 'sent' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : selectedBookingDetails.adminEmailStatus === 'failed' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'}`} data-testid="admin-alert-email-status">
                        {selectedBookingDetails.adminEmailStatus || 'pending'}
                      </span>
                      {typeof selectedBookingDetails.adminSendGridResponseCode === 'number' && (
                        <span className="text-gray-500 text-xs" title="SendGrid HTTP status">SG {selectedBookingDetails.adminSendGridResponseCode}</span>
                      )}
                      {(selectedBookingDetails.adminEmailStatus === 'failed' || selectedBookingDetails.adminEmailStatus === 'pending') && (
                        <button
                          onClick={async () => {
                            try {
                              const res = await resendConfirmationEmail(selectedBookingDetails.id, 'admin');
                              alert(`Admin notification: ${res.adminStatus}` + (res.adminFailureReason ? `\n${res.adminFailureReason}` : (typeof res.adminSendGridResponseCode === 'number' ? `\nSG ${res.adminSendGridResponseCode}` : '')));
                              fetchData();
                            } catch (error) {
                              alert(`Failed to resend: ${error.response?.data?.detail || error.message}`);
                            }
                          }}
                          className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30 text-xs border border-blue-500/30"
                          data-testid="admin-resend-admin-btn"
                          title="Resend admin notification only"
                        >
                          <RefreshCw className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    {selectedBookingDetails.adminEmailSentAt && (
                      <p className="text-gray-500 text-xs mt-1">
                        Sent: {new Date(selectedBookingDetails.adminEmailSentAt).toLocaleString()}
                      </p>
                    )}
                    {selectedBookingDetails.adminEmailStatus === 'failed' && selectedBookingDetails.adminEmailFailureReason && (
                      <p className="text-red-300 text-xs mt-1 break-all" data-testid="admin-alert-failure-reason" title={selectedBookingDetails.adminEmailFailureReason}>
                        ⚠ {selectedBookingDetails.adminEmailFailureReason.slice(0, 140)}
                      </p>
                    )}
                  </div>
                  {selectedBookingDetails.status === 'Completed' && (
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Receipt Email</p>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${selectedBookingDetails.receiptEmailStatus === 'sent' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : selectedBookingDetails.receiptEmailStatus === 'failed' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'}`}>
                          {selectedBookingDetails.receiptEmailStatus || 'pending'}
                        </span>
                        {(selectedBookingDetails.receiptEmailStatus === 'failed' || selectedBookingDetails.receiptEmailStatus === 'pending') && (
                          <button
                            onClick={async () => {
                              try {
                                await resendReceiptEmail(selectedBookingDetails.id);
                                alert('Receipt email resent successfully!');
                                fetchData();
                              } catch (error) {
                                alert(`Failed to resend: ${error.response?.data?.detail || error.message}`);
                              }
                            }}
                            className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30 text-xs border border-blue-500/30"
                            title="Resend receipt email"
                          >
                            <RefreshCw className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      {selectedBookingDetails.receiptEmailSentAt && (
                        <p className="text-gray-500 text-xs mt-1">
                          Sent: {new Date(selectedBookingDetails.receiptEmailSentAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}
                  {selectedBookingDetails.emailFailureReason && (
                    <div className="md:col-span-2">
                      <p className="text-red-400 text-sm">
                        <strong>Email Failure Reason:</strong> {selectedBookingDetails.emailFailureReason}
                      </p>
                    </div>
                  )}
                </div>
              </div>

            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex justify-between items-center pt-6 border-t border-yellow-500/20 sticky bottom-0 bg-gradient-to-br from-gray-900 to-gray-800">
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedBookingDetails(null);
                }}
                className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition font-semibold"
              >
                Close
              </button>
              <div className="flex space-x-3">
                {/* Show Mark Completed button for any non-Completed, non-Cancelled booking */}
                {selectedBookingDetails.status && 
                 selectedBookingDetails.status.toLowerCase() !== 'completed' && 
                 selectedBookingDetails.status.toLowerCase() !== 'cancelled' && (
                  <button
                    onClick={() => {
                      setCompletionData({
                        actualFare: selectedBookingDetails.estimatedFare || selectedBookingDetails.actualFare || '',
                        paymentMethod: selectedBookingDetails.paymentMethod || 'cash',
                        notes: ''
                      });
                      setShowCompletionModal(true);
                    }}
                    className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-semibold flex items-center space-x-2"
                  >
                    <CheckCircle className="w-5 h-5" />
                    <span>Mark Completed</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedBookingForTracking(selectedBookingDetails);
                    setShowTrackingModal(true);
                  }}
                  className="px-6 py-3 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 border border-blue-500/30 transition font-semibold"
                >
                  Initialize Tracking
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this booking?')) {
                      setShowDetailsModal(false);
                      setBookingToDelete(selectedBookingDetails);
                      setShowDeleteModal(true);
                    }
                  }}
                  className="px-6 py-3 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 border border-red-600/30 transition font-semibold"
                >
                  Delete Booking
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Completion Modal */}
      {showCompletionModal && selectedBookingDetails && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl max-w-lg w-full border-2 border-yellow-500/30">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-yellow-400 flex items-center space-x-2">
                  <CheckCircle className="w-6 h-6" />
                  <span>Complete Trip</span>
                </h2>
                <button
                  onClick={() => setShowCompletionModal(false)}
                  className="text-gray-400 hover:text-white transition"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Booking Info */}
                <div className="bg-gray-800/50 p-4 rounded-lg border border-yellow-500/20">
                  <p className="text-gray-400 text-sm">Booking ID</p>
                  <p className="text-white font-bold">{selectedBookingDetails.id}</p>
                  <p className="text-gray-400 text-sm mt-2">Customer</p>
                  <p className="text-white">{selectedBookingDetails.passengerName}</p>
                  <p className="text-gray-400 text-sm mt-2">Route</p>
                  <p className="text-white text-sm">{selectedBookingDetails.pickupLocation} → {selectedBookingDetails.dropoffLocation}</p>
                  <p className="text-gray-400 text-sm mt-2">Estimated Fare</p>
                  <p className="text-yellow-400 font-semibold">${selectedBookingDetails.estimatedFare}</p>
                </div>

                {/* Actual Fare */}
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">
                    Actual Fare Collected <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={completionData.actualFare}
                      onChange={(e) => setCompletionData({ ...completionData, actualFare: e.target.value })}
                      className="w-full pl-8 pr-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                {/* Payment Method */}
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">
                    Payment Method
                  </label>
                  <select
                    value={completionData.paymentMethod}
                    onChange={(e) => setCompletionData({ ...completionData, paymentMethod: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-yellow-500 focus:outline-none capitalize"
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Optional Notes */}
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={completionData.notes}
                    onChange={(e) => setCompletionData({ ...completionData, notes: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-yellow-500 focus:outline-none resize-none"
                    rows="3"
                    placeholder="Any additional notes about the trip..."
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => setShowCompletionModal(false)}
                    className="flex-1 px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition font-semibold"
                    disabled={completing}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCompleteBooking}
                    disabled={completing || !completionData.actualFare}
                    className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {completing ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        <span>Completing...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        <span>Confirm Completion</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Info Note */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-sm text-blue-300">
                  <p className="font-semibold mb-1">What happens next:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>Booking status changes to Completed</li>
                    <li>Receipt email automatically sent to customer</li>
                    <li>Trip appears in completed bookings list</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}




    </div>
  );
};

export default AdminDashboard;
